import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ShieldCheckIcon,
  GlobeAltIcon,
  ChartBarIcon,
  UserGroupIcon,
  CubeIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  FunnelIcon,
  Squares2X2Icon,
  ListBulletIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  HeartIcon,
  ShareIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolidIcon } from '@heroicons/react/24/solid';

interface NFTItem {
  id: string;
  title: string;
  image: string;
  price?: number;
  lastSale?: number;
  owner: string;
  rarity: number;
  isAuction: boolean;
}

const mockNFTs: NFTItem[] = Array.from({ length: 12 }, (_, i) => ({
  id: `${i + 1}`,
  title: `Cosmic Voyager #${1000 + i}`,
  image: `https://picsum.photos/seed/nft-col-${i}/400/400`,
  price: Math.random() > 0.5 ? parseFloat((Math.random() * 10 + 2).toFixed(2)) : undefined,
  lastSale: parseFloat((Math.random() * 8 + 1).toFixed(2)),
  owner: `0x${Math.random().toString(16).slice(2, 6)}...${Math.random().toString(16).slice(2, 6)}`,
  rarity: Math.floor(Math.random() * 100) + 1,
  isAuction: Math.random() > 0.6,
}));

const CollectionDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLiked, setIsLiked] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('price_low');

  // Mock collection data
  const collection = {
    id: id,
    name: 'Cosmic Collection',
    description: 'A journey through the cosmos. 10,000 unique voyagers exploring the infinite universe. Each piece grants exclusive access to the Cosmic DAO and future drops.',
    image: 'https://picsum.photos/seed/collection-detail/200/200',
    bannerImage: 'https://picsum.photos/seed/collection-banner/1200/400',
    creator: {
      name: 'ArtistX',
      avatar: 'https://picsum.photos/seed/creator/100/100',
      verified: true,
      address: '0x1234...5678',
    },
    verified: true,
    stats: {
      floorPrice: 8.2,
      floorChange24h: 12.5,
      volume24h: 156.8,
      volumeChange24h: 25.3,
      totalVolume: 4520,
      owners: 2847,
      items: 10000,
      listed: 342,
      curationScore: 95,
    },
    social: {
      website: 'https://cosmic-collection.io',
      twitter: 'CosmicNFT',
      discord: 'cosmic-collection',
    },
    royalty: 7.5,
  };

  const formatNumber = (num: number) => {
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(2);
  };

  return (
    <div className="min-h-screen">
      {/* Banner */}
      <div className="relative h-64 md:h-80">
        <img
          src={collection.bannerImage}
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dark-900 via-dark-900/50 to-transparent" />
      </div>

      <div className="container-custom">
        {/* Collection Header */}
        <div className="relative -mt-20 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row items-start md:items-end space-y-4 md:space-y-0 md:space-x-6"
          >
            {/* Collection Image */}
            <img
              src={collection.image}
              alt={collection.name}
              className="w-32 h-32 md:w-40 md:h-40 rounded-2xl border-4 border-dark-900 object-cover"
            />

            {/* Collection Info */}
            <div className="flex-1">
              <div className="flex items-center space-x-3 mb-2">
                <h1 className="text-3xl md:text-4xl font-bold">{collection.name}</h1>
                {collection.verified && (
                  <ShieldCheckIcon className="h-7 w-7 text-primary-400" />
                )}
              </div>

              <div className="flex items-center space-x-4 mb-4">
                <Link
                  to={`/profile/${collection.creator.address}`}
                  className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
                >
                  <img
                    src={collection.creator.avatar}
                    alt={collection.creator.name}
                    className="w-6 h-6 rounded-full"
                  />
                  <span>by {collection.creator.name}</span>
                  {collection.creator.verified && (
                    <SparklesIcon className="h-4 w-4 text-primary-400" />
                  )}
                </Link>
                <span className="text-gray-600">•</span>
                <span className="text-gray-400">Royalty: {collection.royalty}%</span>
              </div>

              <p className="text-gray-400 max-w-2xl mb-4">{collection.description}</p>

              {/* Social Links */}
              <div className="flex items-center space-x-4">
                {collection.social.website && (
                  <a
                    href={collection.social.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors"
                  >
                    <GlobeAltIcon className="h-5 w-5" />
                  </a>
                )}
                {collection.social.twitter && (
                  <a
                    href={`https://twitter.com/${collection.social.twitter}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                  </a>
                )}
                {collection.social.discord && (
                  <a
                    href={`https://discord.gg/${collection.social.discord}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 rounded-lg bg-dark-700 hover:bg-dark-600 transition-colors"
                  >
                    <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                    </svg>
                  </a>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setIsLiked(!isLiked)}
                className="p-3 rounded-xl bg-dark-700 hover:bg-dark-600 transition-colors"
              >
                {isLiked ? (
                  <HeartSolidIcon className="h-6 w-6 text-red-500" />
                ) : (
                  <HeartIcon className="h-6 w-6" />
                )}
              </button>
              <button className="p-3 rounded-xl bg-dark-700 hover:bg-dark-600 transition-colors">
                <ShareIcon className="h-6 w-6" />
              </button>
              <button className="p-3 rounded-xl bg-dark-700 hover:bg-dark-600 transition-colors">
                <EllipsisHorizontalIcon className="h-6 w-6" />
              </button>
            </div>
          </motion.div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4 mb-8"
        >
          {[
            { label: 'Floor', value: `${collection.stats.floorPrice} ETH`, change: collection.stats.floorChange24h },
            { label: 'Volume (24h)', value: `${formatNumber(collection.stats.volume24h)} ETH`, change: collection.stats.volumeChange24h },
            { label: 'Total Volume', value: `${formatNumber(collection.stats.totalVolume)} ETH` },
            { label: 'Items', value: formatNumber(collection.stats.items) },
            { label: 'Owners', value: formatNumber(collection.stats.owners) },
            { label: 'Listed', value: `${collection.stats.listed} (${((collection.stats.listed / collection.stats.items) * 100).toFixed(1)}%)` },
            { label: 'Unique Owners', value: `${((collection.stats.owners / collection.stats.items) * 100).toFixed(0)}%` },
            { label: 'Curation Score', value: collection.stats.curationScore, isCuration: true },
          ].map((stat, index) => (
            <div key={index} className="glass-card p-4 text-center">
              <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
              <p className={`text-lg font-bold ${stat.isCuration && stat.value >= 90 ? 'text-green-400' : ''}`}>
                {stat.value}
              </p>
              {stat.change !== undefined && (
                <p className={`text-xs flex items-center justify-center ${stat.change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {stat.change >= 0 ? (
                    <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                  )}
                  {Math.abs(stat.change)}%
                </p>
              )}
            </div>
          ))}
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex flex-col md:flex-row gap-4 mb-8"
        >
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-500" />
            <input
              type="text"
              placeholder="Search by name or token ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
            />
          </div>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl focus:outline-none focus:border-primary-500"
          >
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="recent">Recently Listed</option>
            <option value="rarity">Rarity: Rare to Common</option>
          </select>

          <button className="flex items-center space-x-2 px-4 py-3 bg-dark-800/50 border border-white/10 rounded-xl hover:border-white/20 transition-colors">
            <FunnelIcon className="h-5 w-5" />
            <span>Filters</span>
          </button>

          <div className="flex items-center space-x-1 bg-dark-800/50 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'grid' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <Squares2X2Icon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors ${
                viewMode === 'list' ? 'bg-primary-500 text-white' : 'text-gray-400 hover:text-white'
              }`}
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
          </div>
        </motion.div>

        {/* NFTs Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
        >
          {mockNFTs.map((nft, index) => (
            <Link
              key={nft.id}
              to={`/auctions/${nft.id}`}
              className="glass-card overflow-hidden group hover:border-primary-500/50 transition-all duration-300"
            >
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={nft.image}
                  alt={nft.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                {nft.isAuction && (
                  <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-primary-500/20 text-primary-400 text-xs font-medium backdrop-blur-sm">
                    On Auction
                  </div>
                )}
                <div className="absolute top-3 right-3 px-2 py-1 rounded-full bg-dark-900/60 text-xs font-medium backdrop-blur-sm">
                  #{nft.rarity}
                </div>
              </div>
              <div className="p-4">
                <h3 className="font-semibold group-hover:text-primary-400 transition-colors truncate mb-2">
                  {nft.title}
                </h3>
                <div className="flex items-center justify-between">
                  {nft.price ? (
                    <div>
                      <p className="text-xs text-gray-500">Price</p>
                      <p className="font-semibold">{nft.price} ETH</p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-xs text-gray-500">Last Sale</p>
                      <p className="text-gray-400">{nft.lastSale} ETH</p>
                    </div>
                  )}
                  <div className="text-right">
                    <p className="text-xs text-gray-500">Owner</p>
                    <p className="text-sm text-gray-400">{nft.owner}</p>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </motion.div>

        {/* Load More */}
        <div className="text-center mt-12">
          <button className="btn-secondary">Load More</button>
        </div>
      </div>
    </div>
  );
};

export default CollectionDetailPage;
